import { supabase } from '../../../shared/lib/supabase';

// Define the TeamAccess interface
export interface TeamAccess {
  id: string;
  team_id: string;
  organisation_id: string;
  access_type: string;
  context: string | null;
  granted_by: string | null;
  granted_at: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  // Joined fields
  team_name?: string;
  organisation_name?: string;
}

export interface TeamAccessCreateInput {
  team_id: string;
  organisation_id: string;
  access_type: string;
  context?: string | null;
  granted_by?: string | null;
  granted_at: string;
  active: boolean;
}

export interface TeamAccessUpdateInput {
  team_id?: string;
  access_type?: string;
  context?: string | null;
  granted_by?: string | null;
  granted_at?: string;
  active?: boolean;
}

class TeamAccessService {
  async getAll(): Promise<TeamAccess[]> {
    try {
      console.log('🔍 Fetching team access with joins...');

      const { data, error } = await supabase
        .from('team_access')
        .select(`
          *,
          teams!team_access_team_id_fkey(name),
          organisations!team_access_organisation_id_fkey(name)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching team access:', error);
        throw error;
      }

      const transformed = (data || []).map((item: any) => ({
        ...item,
        team_name: item.teams?.name || item.team_name,
        organisation_name: item.organisations?.name || item.organisation_name
      }));

      console.log('✅ Team access fetched successfully:', transformed.length, 'records');
      return transformed;
    } catch (error) {
      console.error('❌ Error in getAll:', error);
      throw error;
    }
  }

  async getById(id: string): Promise<TeamAccess | null> {
    try {
      console.log('🔍 Fetching team access by ID:', id);

      const { data, error } = await supabase
        .from('team_access')
        .select(`
          *,
          teams!team_access_team_id_fkey(name),
          organisations!team_access_organisation_id_fkey(name)
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('❌ Error fetching team access by ID:', error);
        throw error;
      }

      const transformed = data
        ? { ...data, team_name: (data as any).teams?.name, organisation_name: (data as any).organisations?.name }
        : null;

      console.log('✅ Team access fetched successfully');
      return transformed as any;
    } catch (error) {
      console.error('❌ Error in getById:', error);
      throw error;
    }
  }

  async create(input: TeamAccessCreateInput): Promise<TeamAccess> {
    try {
      console.log('🔍 Creating team access:', input);

      const { data, error } = await supabase
        .from('team_access')
        .insert([input])
        .select(`
          *,
          teams!team_access_team_id_fkey(name),
          organisations!team_access_organisation_id_fkey(name)
        `)
        .single();

      if (error) {
        console.error('❌ Error creating team access:', error);
        throw error;
      }

      const transformed = data
        ? { ...data, team_name: (data as any).teams?.name, organisation_name: (data as any).organisations?.name }
        : (data as any);

      console.log('✅ Team access created successfully');
      return transformed as any;
    } catch (error) {
      console.error('❌ Error in create:', error);
      throw error;
    }
  }

  async update(id: string, input: TeamAccessUpdateInput): Promise<TeamAccess> {
    try {
      console.log('🔍 Updating team access:', id, input);

      const { data, error } = await supabase
        .from('team_access')
        .update(input)
        .eq('id', id)
        .select(`
          *,
          teams!team_access_team_id_fkey(name),
          organisations!team_access_organisation_id_fkey(name)
        `)
        .single();

      if (error) {
        console.error('❌ Error updating team access:', error);
        throw error;
      }

      const transformed = data
        ? { ...data, team_name: (data as any).teams?.name, organisation_name: (data as any).organisations?.name }
        : (data as any);

      console.log('✅ Team access updated successfully');
      return transformed as any;
    } catch (error) {
      console.error('❌ Error in update:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      console.log('🔍 Deleting team access:', id);

      const { error } = await supabase
        .from('team_access')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('❌ Error deleting team access:', error);
        throw error;
      }

      console.log('✅ Team access deleted successfully');
    } catch (error) {
      console.error('❌ Error in delete:', error);
      throw error;
    }
  }
}

export const teamAccessService = new TeamAccessService();
